import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { GIORNI_LABELS } from "../../utils/terapieExtraFormat";

const EMPTY_FORM = {
  nome: "",
  dosaggio: "",
  note: "",
  data_inizio: "",
  data_fine: "",
  giorni_settimana: [],
  orari: ["08:00"],
};

export default function TerapiaExtraDialog({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  saving = false,
  dialogTitle,
  dialogDescription,
  submitCreateLabel = "Crea terapia",
  submitEditLabel = "Salva modifiche",
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        nome: initialData.nome || "",
        dosaggio: initialData.dosaggio || "",
        note: initialData.note || "",
        data_inizio: initialData.data_inizio || "",
        data_fine: initialData.data_fine || "",
        giorni_settimana: [...(initialData.giorni_settimana || [])],
        orari: initialData.orari?.length ? [...initialData.orari] : ["08:00"],
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, initialData]);

  const toggleGiorno = (day) => {
    setForm((prev) => {
      const set = new Set(prev.giorni_settimana);
      if (set.has(day)) set.delete(day);
      else set.add(day);
      return { ...prev, giorni_settimana: [...set].sort((a, b) => a - b) };
    });
  };

  const updateOrario = (index, value) => {
    setForm((prev) => {
      const orari = [...prev.orari];
      orari[index] = value;
      return { ...prev, orari };
    });
  };

  const addOrario = () => {
    setForm((prev) => ({ ...prev, orari: [...prev.orari, "12:00"] }));
  };

  const removeOrario = (index) => {
    setForm((prev) => {
      if (prev.orari.length <= 1) return prev;
      return { ...prev, orari: prev.orari.filter((_, i) => i !== index) };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      nome: form.nome.trim(),
      dosaggio: form.dosaggio.trim() || null,
      note: form.note.trim() || null,
      data_inizio: form.data_inizio,
      data_fine: form.data_fine || null,
      giorni_settimana: form.giorni_settimana,
      orari: form.orari.filter(Boolean),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {dialogTitle || (initialData ? "Modifica terapia extra" : "Nuova terapia extra")}
          </DialogTitle>
          <DialogDescription>
            {dialogDescription ||
              "Pianifica giorni della settimana e uno o più orari di somministrazione."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="te-nome">Nome terapia *</Label>
            <Input
              id="te-nome"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Es. Augmentin 1g"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="te-dosaggio">Dosaggio</Label>
            <Input
              id="te-dosaggio"
              value={form.dosaggio}
              onChange={(e) => setForm((f) => ({ ...f, dosaggio: e.target.value }))}
              placeholder="Es. 1 compressa"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="te-inizio">Data inizio *</Label>
              <Input
                id="te-inizio"
                type="date"
                value={form.data_inizio}
                onChange={(e) => setForm((f) => ({ ...f, data_inizio: e.target.value }))}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="te-fine">Data fine</Label>
              <Input
                id="te-fine"
                type="date"
                value={form.data_fine}
                onChange={(e) => setForm((f) => ({ ...f, data_fine: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Giorni della settimana</Label>
            <p className="text-xs text-slate-500 mb-2">Nessuna selezione = tutti i giorni</p>
            <div className="flex flex-wrap gap-2">
              {GIORNI_LABELS.map((label, idx) => (
                <label
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer hover:bg-slate-50"
                >
                  <Checkbox
                    checked={form.giorni_settimana.includes(idx)}
                    onCheckedChange={() => toggleGiorno(idx)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Orari di somministrazione *</Label>
            <div className="space-y-2">
              {form.orari.map((orario, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={orario}
                    onChange={(e) => updateOrario(idx, e.target.value)}
                    required
                    className="w-36"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeOrario(idx)}
                    disabled={form.orari.length <= 1}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addOrario} className="mt-2">
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi orario
            </Button>
          </div>
          <div>
            <Label htmlFor="te-note">Note</Label>
            <Textarea
              id="te-note"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={2}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {initialData ? submitEditLabel : submitCreateLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
